/** Saved on the book; `system` follows OS light/dark default presets. */
export type UiThemePreset =
  | "light-warm"
  | "light-clean"
  | "light-paper"
  | "dark-neutral"
  | "dark-warm"
  | "dark-oled";

export type ThemeMode = "system" | UiThemePreset;

export const LIGHT_PRESETS: { id: UiThemePreset; label: string }[] = [
  { id: "light-clean", label: "Clean (light)" },
  { id: "light-warm", label: "Warm (light)" },
  { id: "light-paper", label: "Paper (light)" },
];

export const DARK_PRESETS: { id: UiThemePreset; label: string }[] = [
  { id: "dark-neutral", label: "Neutral (dark)" },
  { id: "dark-warm", label: "Warm (dark)" },
  { id: "dark-oled", label: "OLED (dark)" },
];

const ALL_PRESET_IDS = new Set<UiThemePreset>([
  ...LIGHT_PRESETS.map((p) => p.id),
  ...DARK_PRESETS.map((p) => p.id),
]);

/** When theme is `system`, pick these presets from OS preference. */
export const SYSTEM_LIGHT_PRESET: UiThemePreset = "light-clean";
export const SYSTEM_DARK_PRESET: UiThemePreset = "dark-neutral";

export function isUiThemePreset(value: string): value is UiThemePreset {
  return ALL_PRESET_IDS.has(value as UiThemePreset);
}

export function normalizeThemeMode(value: unknown): ThemeMode {
  if (value === "system") return "system";
  if (value === "light") return "light-warm";
  if (value === "dark") return "dark-warm";
  if (typeof value === "string" && isUiThemePreset(value)) return value;
  return "system";
}

/** Value for `data-theme` on the app root. */
export function resolveThemeDataAttribute(theme: ThemeMode, systemPrefersDark: boolean): UiThemePreset {
  if (theme === "system") return systemPrefersDark ? SYSTEM_DARK_PRESET : SYSTEM_LIGHT_PRESET;
  return theme;
}
